/**
 * AIRTABLE IMPORT COMPONENT
 * 
 * Direct Airtable API integration for importing collections with photos.
 * Fetches fresh attachment URLs at import time to avoid expiration issues.
 * 
 * Flow:
 * 1. Enter API key (with security reassurances)
 * 2. Select base
 * 3. Select table
 * 4. Choose item type
 * 5. Map fields (NO auto-apply, just hints)
 * 6. Preview data
 * 7. Review data quality (PreImportReview) - NEW!
 * 8. Import (with photo download)
 * 9. Download error report if any failures
 * 
 * Features:
 * - Item type selection (polish, plate, transfer, etc.)
 * - Fields default to "Skip" not auto-applied
 * - Suggestions shown as hints, not auto-applied
 * - Pre-import review for data quality checks
 * - Error report download for skipped items
 * - Duplicate mapping detection
 * - 70+ fields supported for polish imports
 */

import { useState, useMemo } from 'react';
import {
  ArrowLeft, ArrowRight, X, AlertCircle, Loader2, CheckCircle2,
  Key, Database, Table, Image, Shield, ExternalLink,
  Eye, EyeOff, HelpCircle, AlertTriangle, Download, FileText, HardDrive
} from 'lucide-react';
import { collection, addDoc, Timestamp, getDocs, query, where, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { getCurrentUser } from '../firebase/auth';
import PreImportReview from './PreImportReview';
import {
  validateApiKey,
  listBases,
  getBaseSchema,
  fetchAllRecords,
  analyzeRecords,
  extractAttachments,
  downloadAttachment,
  detectLinkedRecordFields,
  buildLinkedRecordCache,
  resolveLinkedRecordValue,
  type AirtableBase,
  type AirtableTable,
  type AirtableRecord
} from '../services/airtableService';
import { detectPolishType, normalizePolishType } from '../utils/csvUtils';
import { compressImageBlob } from '../utils/imageUtils';
import { useStorageUsage } from '../hooks/useStorageUsage';
import StorageUpgradeModal from './StorageUpgradeModal';

type ImportStep = 'api-key' | 'select-base' | 'select-table' | 'select-type' | 'mapping' | 'preview' | 'review' | 'importing' | 'complete';

// Supported item types
type ImportItemType = 'polish' | 'plate' | 'transfer' | 'embellishment' | 'tool' | 'powder' | 'acrylic';

// Item type display info
const ITEM_TYPE_INFO: Record<ImportItemType, { label: string; emoji: string; description: string }> = {
  polish: { label: 'Nail Polish', emoji: '💅', description: 'Nail lacquers, gel polishes, etc.' },
  plate: { label: 'Stamping Plates', emoji: '🔲', description: 'Metal stamping plates for nail art' },
  transfer: { label: 'Decals & Transfers', emoji: '🎨', description: 'Stickers, waterslides, vinyl, tattoos' },
  embellishment: { label: 'Embellishments', emoji: '✨', description: 'Charms, rhinestones, foils, glitter' },
  tool: { label: 'Tools', emoji: '🛠️', description: 'Brushes, dotting tools, lamps, etc.' },
  powder: { label: 'Powders', emoji: '🌟', description: 'Chrome, aurora, buffing powders' },
  acrylic: { label: 'Acrylics', emoji: '💎', description: 'Acrylic powders, liquids, tips' },
};

// Base fields shared by all item types
const BASE_FIELDS: Record<string, { label: string; required: boolean }> = {
  brand: { label: 'Brand', required: true },
  name: { label: 'Name', required: true },
  photos: { label: 'Photos (attachments)', required: false },
  quantity: { label: 'Quantity', required: false },
  notes: { label: 'Notes', required: false },
  publicNotes: { label: 'Public Notes', required: false },
  tags: { label: 'Tags', required: false },
  isFavorite: { label: 'Favorite', required: false },
  price: { label: 'Price', required: false },
  storage: { label: 'Storage Location', required: false },
  barcode: { label: 'Barcode/SKU/UPC', required: false },
  swatched: { label: 'Swatched (Y/N)', required: false },
  timesUsed: { label: 'Times Used / Worn', required: false },
  lastUsedDate: { label: 'Last Used Date', required: false },
};

// Type-specific fields
const TYPE_SPECIFIC_FIELDS: Record<ImportItemType, Record<string, { label: string; required: boolean }>> = {
  polish: {
    // Basic
    colorDescription: { label: 'Color Description', required: false },
    line: { label: 'Line (e.g., Insta-Dri, Ultra Chrome)', required: false },
    swatchNumber: { label: 'Swatch Number', required: false },
    polishType: { label: 'Polish Type (Nail Lacquer/Gel/etc.)', required: false },
    
    // Release system
    releaseType: { label: 'Release Type', required: false },
    releaseYear: { label: 'Release Year', required: false },
    releaseSeason: { label: 'Release Season', required: false },
    
    // Collection
    collectionName: { label: 'Collection Name', required: false },
    collection: { label: 'Collection (alias)', required: false },
    
    // Event
    eventSource: { label: 'Event Name (PPU/HHC/etc.)', required: false },
    eventTheme: { label: 'Event Theme', required: false },
    
    // Box
    boxName: { label: 'Box Name', required: false },
    boxTheme: { label: 'Box Theme', required: false },
    
    // Group
    groupName: { label: 'Group Name', required: false },
    groupTheme: { label: 'Group Theme', required: false },
    
    // Exclusive
    exclusiveTo: { label: 'Exclusive To', required: false },
    exclusiveTheme: { label: 'Exclusive Theme', required: false },
    
    // Charity
    charityName: { label: 'Charity Name', required: false },
    charityCause: { label: 'Charity Cause', required: false },
    
    // Convention
    conventionName: { label: 'Convention Name', required: false },
    
    // Collab
    collabWith: { label: 'Collab With', required: false },
    collabTheme: { label: 'Collab Theme', required: false },
    
    // Set/Duo
    setName: { label: 'Set Name (duo/trio)', required: false },
    setType: { label: 'Set Type (duo/trio/quad/set)', required: false },
    
    // Re-release
    isRerelease: { label: 'Is Re-release (Y/N)', required: false },
    originalReleaseYear: { label: 'Original Release Year', required: false },
    originalReleaseSeason: { label: 'Original Release Season', required: false },
    releaseNotes: { label: 'Release Notes', required: false },
    
    // Structured tags
    baseType: { label: 'Base Type (creme/crelly/jelly/sheer)', required: false },
    finish: { label: 'Finish/Effect', required: false },
    holoType: { label: 'Holo Type', required: false },
    colorShift: { label: 'Color Shift (duochrome/multichrome)', required: false },
    particles: { label: 'Particles (glitter/flakes)', required: false },
    specialFeatures: { label: 'Special Features (magnetic/thermal)', required: false },
    colorFamily: { label: 'Color Family', required: false },
    utility: { label: 'Utility (topper/undie)', required: false },
    colorCharacter: { label: 'Color Character (bright/neon/pastel)', required: false },
    
    // Status
    hasSwatched: { label: 'Has Swatched (Y/N)', required: false },
    isRareLE: { label: 'Rare/LE', required: false },
    onWishlist: { label: 'Wishlist', required: false },
    isoPriority: { label: 'ISO Priority', required: false },
    
    // Destash
    isForDestash: { label: 'For Destash (Y/N)', required: false },
    destashPrice: { label: 'Destash Price', required: false },
    destashCondition: { label: 'Destash Condition', required: false },
  },
  plate: {
    plateSize: { label: 'Plate Size', required: false },
    plateShape: { label: 'Plate Shape', required: false },
    imageCount: { label: 'Number of Images', required: false },
    collection: { label: 'Collection/Series', required: false },
  },
  transfer: {
    transferType: { label: 'Transfer Type', required: false },
    collection: { label: 'Collection/Series', required: false },
  },
  embellishment: {
    embellishmentType: { label: 'Embellishment Type', required: false },
    color: { label: 'Color', required: false },
    size: { label: 'Size', required: false },
  },
  tool: {
    toolType: { label: 'Tool Type', required: false },
    material: { label: 'Material', required: false },
  },
  powder: {
    powderType: { label: 'Powder Type', required: false },
    color: { label: 'Color', required: false },
    effect: { label: 'Effect', required: false },
  },
  acrylic: {
    acrylicType: { label: 'Acrylic Type', required: false },
    color: { label: 'Color', required: false },
  },
};

// Get all available fields for a specific item type
function getFieldsForType(itemType: ImportItemType): Record<string, { label: string; required: boolean }> {
  return {
    ...BASE_FIELDS,
    ...TYPE_SPECIFIC_FIELDS[itemType],
  };
}

interface FieldMapping {
  [airtableField: string]: string | null;
}

// Track skipped/failed records for error report
interface SkippedRecord {
  rowNum: number;
  brand: string;
  name: string;
  reason: string;
  airtableRecordId: string;
}

interface Props {
  onComplete: () => void;
  onClose: () => void;
}

export default function AirtableImport({ onComplete, onClose }: Props) {
  // Step management
  const [currentStep, setCurrentStep] = useState<ImportStep>('api-key');
  
  // API key
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [validatingKey, setValidatingKey] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);
  
  // Base selection
  const [bases, setBases] = useState<AirtableBase[]>([]);
  const [selectedBase, setSelectedBase] = useState<AirtableBase | null>(null);
  const [loadingBases, setLoadingBases] = useState(false);
  
  // Table selection
  const [tables, setTables] = useState<AirtableTable[]>([]);
  const [selectedTable, setSelectedTable] = useState<AirtableTable | null>(null);
  const [loadingTables, setLoadingTables] = useState(false);
  
  // Item type selection
  const [selectedItemType, setSelectedItemType] = useState<ImportItemType>('polish');
  
  // Records & Analysis
  const [records, setRecords] = useState<AirtableRecord[]>([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recordsFetched, setRecordsFetched] = useState(0);
  const [analysis, setAnalysis] = useState<{
    fields: { name: string; type: string; sampleValues: string[]; suggestedMapping: string | null }[];
    attachmentFields: string[];
    recordCount: number;
  } | null>(null);
  
  // Field mapping - ALL fields start as null (skip)
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>({});
  
  // Import state
  const [importProgress, setImportProgress] = useState(0);
  const [importedCount, setImportedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const [photoCount, setPhotoCount] = useState(0);
  const [photoErrors, setPhotoErrors] = useState(0);
  const [importError, setImportError] = useState<string | null>(null);
  const [currentStatus, setCurrentStatus] = useState('');
  const [skippedRecords, setSkippedRecords] = useState<SkippedRecord[]>([]);

  // Linked record resolution
  const [linkedFields, setLinkedFields] = useState<Map<string, string>>(new Map());
  const [linkedRecordCache, setLinkedRecordCache] = useState<Map<string, Map<string, string>>>(new Map());
  const [resolvingLinkedRecords, setResolvingLinkedRecords] = useState(false);

  // Mapped rows for pre-import review
  const [mappedRowsForReview, setMappedRowsForReview] = useState<Record<string, any>[]>([]);

  // Storage tracking for photo uploads
  const { usedBytes, limitBytes, nextTier } = useStorageUsage();
  const [photosSkippedStorage, setPhotosSkippedStorage] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Get current target fields based on selected item type
  const targetFields = useMemo(() => getFieldsForType(selectedItemType), [selectedItemType]);

  // Check for duplicate mappings (multiple source fields mapping to same target)
  const getDuplicateMappings = () => {
    const targetCounts: Record<string, string[]> = {};
    for (const [airtableField, targetField] of Object.entries(fieldMapping)) {
      if (targetField) {
        if (!targetCounts[targetField]) {
          targetCounts[targetField] = [];
        }
        targetCounts[targetField].push(airtableField);
      }
    }
    return Object.entries(targetCounts)
      .filter(([_, sources]) => sources.length > 1)
      .map(([target, sources]) => ({ target, sources }));
  };

  // Check if required fields are mapped
  const getMissingRequired = () => {
    const mapped = new Set(Object.values(fieldMapping).filter(Boolean));
    return Object.entries(targetFields)
      .filter(([key, config]) => config.required && !mapped.has(key))
      .map(([key]) => key);
  };

  // Get the photo field (if any)
  const getPhotoField = () => {
    return Object.entries(fieldMapping).find(([_, target]) => target === 'photos')?.[0];
  };

  // Count records with photos
  const getRecordsWithPhotos = () => {
    const photoField = getPhotoField();
    if (!photoField) return 0;

    return records.filter(r => {
      const attachments = extractAttachments(r.fields[photoField]);
      return attachments.length > 0;
    }).length;
  };

  // Validate API key and load bases
  const handleValidateKey = async () => {
    if (!apiKey.trim()) {
      setKeyError('Please enter your API key');
      return;
    }
    
    setValidatingKey(true);
    setKeyError(null);
    
    try {
      const validation = await validateApiKey(apiKey);
      
      if (!validation.valid) {
        setKeyError(validation.error || 'Invalid API key');
        return;
      }
      
      // Load bases
      setLoadingBases(true);
      const basesData = await listBases(apiKey);
      setBases(basesData);
      setCurrentStep('select-base');
    } catch (error) {
      setKeyError('Failed to connect to Airtable. Please check your API key.');
    } finally {
      setValidatingKey(false);
      setLoadingBases(false);
    }
  };

  // Select a base and load its tables
  const handleSelectBase = async (base: AirtableBase) => {
    setSelectedBase(base);
    setLoadingTables(true);
    
    try {
      const tablesData = await getBaseSchema(apiKey, base.id);
      setTables(tablesData);
      setCurrentStep('select-table');
    } catch (error) {
      console.error('Failed to load tables:', error);
    } finally {
      setLoadingTables(false);
    }
  };

  // Select a table and fetch records
  const handleSelectTable = async (table: AirtableTable) => {
    setSelectedTable(table);
    setLoadingRecords(true);
    setRecordsFetched(0);
    setResolvingLinkedRecords(false);
    
    try {
      const recordsData = await fetchAllRecords(
        apiKey,
        selectedBase!.id,
        table.id,
        (count) => setRecordsFetched(count)
      );
      
      setRecords(recordsData);
      
      // Detect linked record fields from schema
      const detectedLinkedFields = detectLinkedRecordFields(tables, table.id);
      setLinkedFields(detectedLinkedFields);
      
      // If there are linked record fields, build the cache to resolve them
      if (detectedLinkedFields.size > 0) {
        setResolvingLinkedRecords(true);
        setCurrentStatus('Resolving linked records...');
        
        const cache = await buildLinkedRecordCache(
          apiKey,
          selectedBase!.id,
          recordsData,
          tables,
          table.id,
          (status) => setCurrentStatus(status)
        );
        
        setLinkedRecordCache(cache);
        setResolvingLinkedRecords(false);
        setCurrentStatus('');
      }
      
      // Analyze the records
      const analysisResult = analyzeRecords(recordsData, tables, table.id);
      setAnalysis(analysisResult);
      
      // IMPORTANT: Initialize ALL fields to null (skip) - DON'T auto-apply suggestions!
      // This was causing the "scrambled brands" bug
      const initialMapping: FieldMapping = {};
      for (const field of analysisResult.fields) {
        initialMapping[field.name] = null; // All default to skip!
      }
      setFieldMapping(initialMapping);
      
      // Go to item type selection first
      setCurrentStep('select-type');
    } catch (error) {
      console.error('Failed to fetch records:', error);
    } finally {
      setLoadingRecords(false);
      setResolvingLinkedRecords(false);
      setCurrentStatus('');
    }
  };

  // Update field mapping
  const updateMapping = (airtableField: string, targetField: string | null) => {
    setFieldMapping(prev => ({ ...prev, [airtableField]: targetField || null }));
  };

  // Generate and download error report CSV
  const downloadErrorReport = () => {
    if (skippedRecords.length === 0) return;
    
    const headers = ['Row', 'Brand', 'Name', 'Reason', 'Airtable Record ID'];
    const rows = skippedRecords.map(r => [
      String(r.rowNum),
      `"${(r.brand || '').replace(/"/g, '""')}"`,
      `"${(r.name || '').replace(/"/g, '""')}"`,
      `"${r.reason.replace(/"/g, '""')}"`,
      r.airtableRecordId
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `airtable-import-errors-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Prepare mapped rows for pre-import review
  const prepareMappedRows = (): Record<string, any>[] => {
    return records.map(record => {
      const mappedData: Record<string, any> = {
        _errors: [],
        _raw: record.fields,
      };
      
      for (const [airtableField, targetField] of Object.entries(fieldMapping)) {
        if (!targetField || targetField === 'photos') continue;
        
        let value = record.fields[airtableField];
        if (value === null || value === undefined) continue;
        
        // Resolve linked records
        value = resolveLinkedRecordValue(value, airtableField, linkedFields, linkedRecordCache);
        
        // Handle different value types (same as in handleImport)
        if (['tags', 'finish', 'colorFamily', 'holoType', 'particles', 'specialFeatures', 'utility', 'colorCharacter'].includes(targetField)) {
          if (Array.isArray(value)) {
            mappedData[targetField] = value.map(v => String(v));
          } else if (typeof value === 'string') {
            mappedData[targetField] = value.split(',').map(s => s.trim()).filter(Boolean);
          }
        } else if (['isFavorite', 'onWishlist', 'isRareLE', 'isRerelease', 'isForDestash', 'hasSwatched', 'swatched'].includes(targetField)) {
          const strVal = String(value).toLowerCase().trim();
          mappedData[targetField] = strVal === 'y' || strVal === 'yes' || strVal === 'true' || strVal === '1' || value === true || value === 1;
        } else if (targetField === 'timesUsed') {
          const strVal = String(value).toLowerCase().trim();
          if (strVal === 'y' || strVal === 'yes') {
            mappedData[targetField] = 1;
          } else {
            const numVal = parseInt(String(value));
            mappedData[targetField] = !isNaN(numVal) && numVal > 0 ? numVal : 0;
          }
        } else if (['quantity', 'imageCount', 'releaseYear', 'originalReleaseYear'].includes(targetField)) {
          const numVal = parseInt(String(value));
          if (!isNaN(numVal)) {
            mappedData[targetField] = numVal;
          }
        } else if (['price', 'destashPrice'].includes(targetField)) {
          const cleanPrice = String(value).replace(/[$£€,]/g, '');
          const priceVal = parseFloat(cleanPrice);
          if (!isNaN(priceVal) && priceVal >= 0) {
            mappedData[targetField] = priceVal;
          }
        } else if (targetField === 'polishType') {
          // Normalize polish type
          const normalized = normalizePolishType(String(value));
          mappedData[targetField] = normalized || String(value);
        } else {
          mappedData[targetField] = String(value);
        }
      }
      
      // Auto-detect polish type if not set (only for polish imports)
      if (selectedItemType === 'polish' && !mappedData.polishType && mappedData.brand && mappedData.name) {
        const detected = detectPolishType(mappedData.name, mappedData.brand);
        if (detected) {
          mappedData.polishType = detected;
        }
      }
      
      return mappedData;
    });
  };

  // Proceed to review step
  const handleProceedToReview = () => {
    const mapped = prepareMappedRows();
    setMappedRowsForReview(mapped);
    setCurrentStep('review');
  };

  // Handle import
  const handleImport = async () => {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      setImportError('Please log in to import');
      return;
    }
    
    setCurrentStep('importing');
    setImportProgress(0);
    setImportedCount(0);
    setSkippedCount(0);
    setPhotoCount(0);
    setPhotoErrors(0);
    setImportError(null);
    setSkippedRecords([]);
    
    const photoField = getPhotoField();
    const totalRecords = records.length;
    const skippedList: SkippedRecord[] = [];
    
    // Get the field names mapped to brand and name for error reporting
    const brandFieldName = Object.entries(fieldMapping).find(([_, t]) => t === 'brand')?.[0];
    const nameFieldName = Object.entries(fieldMapping).find(([_, t]) => t === 'name')?.[0];
    
    try {
      // Check for existing items of THIS type
      const existingQuery = query(
        collection(db, 'users', currentUser.uid, 'items'),
        where('itemType', '==', selectedItemType)
      );
      const existingDocs = await getDocs(existingQuery);
      const existingKeys = new Set(
        existingDocs.docs.map(doc => {
          const data = doc.data();
          return `${data.brand?.toLowerCase()}|${data.name?.toLowerCase()}`;
        })
      );
      
      let imported = 0;
      let skipped = 0;
      let photos = 0;
      let photoErrs = 0;
      let sessionBytes = 0;   // Track bytes uploaded this import session
      let storageSkipped = 0; // Track photos skipped due to storage limit
      
      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        const rowNum = i + 1;
        
        // Get brand and name values for error reporting
        const brandValue = brandFieldName ? record.fields[brandFieldName] : null;
        const nameValue = nameFieldName ? record.fields[nameFieldName] : null;
        const brandStr = brandValue != null ? String(brandValue) : '';
        const nameStr = nameValue != null ? String(nameValue) : '';
        
        // Use pre-mapped data from review step if available, otherwise map from scratch
        let mappedData: Record<string, any>;
        
        if (mappedRowsForReview.length > 0 && mappedRowsForReview[i]) {
          // Use the reviewed/updated row data (remove internal fields)
          const { _errors, _raw, ...cleanData } = mappedRowsForReview[i];
          mappedData = { ...cleanData };
        } else {
          // Map the fields from scratch (fallback)
          mappedData = {};
          
          for (const [airtableField, targetField] of Object.entries(fieldMapping)) {
            if (!targetField || targetField === 'photos') continue;
            
            let value = record.fields[airtableField];
            if (value === null || value === undefined) continue;
            
            // Resolve linked records to their display names
            value = resolveLinkedRecordValue(value, airtableField, linkedFields, linkedRecordCache);
            
            // Handle different value types
            
            // Array fields - structured tags
            if (['tags', 'finish', 'colorFamily', 'holoType', 'particles', 'specialFeatures', 'utility', 'colorCharacter'].includes(targetField)) {
              if (Array.isArray(value)) {
                mappedData[targetField] = value.map(v => String(v));
              } else if (typeof value === 'string') {
                mappedData[targetField] = value.split(',').map(s => s.trim()).filter(Boolean);
              }
            } 
            // Boolean fields
            else if (['isFavorite', 'onWishlist', 'isRareLE', 'isRerelease', 'isForDestash', 'hasSwatched'].includes(targetField)) {
              const strVal = String(value).toLowerCase().trim();
              mappedData[targetField] = strVal === 'y' || strVal === 'yes' || strVal === 'true' || strVal === '1' || value === true || value === 1;
            } 
            // Swatched field
            else if (targetField === 'swatched') {
              const strVal = String(value).toLowerCase().trim();
              mappedData[targetField] = strVal === 'y' || strVal === 'yes' || strVal === 'true' || strVal === '1' || value === true || value === 1;
            } 
            // Times used
            else if (targetField === 'timesUsed') {
              const strVal = String(value).toLowerCase().trim();
              if (strVal === 'y' || strVal === 'yes') {
                mappedData[targetField] = 1;
              } else {
                const numVal = parseInt(String(value));
                mappedData[targetField] = !isNaN(numVal) && numVal > 0 ? numVal : 0;
              }
            } 
            // Integer fields
            else if (['quantity', 'imageCount', 'releaseYear', 'originalReleaseYear'].includes(targetField)) {
              const numVal = parseInt(String(value));
              if (!isNaN(numVal)) {
                mappedData[targetField] = numVal;
              }
            } 
            // Price fields
            else if (['price', 'destashPrice'].includes(targetField)) {
              const cleanPrice = String(value).replace(/[$£€,]/g, '');
              const priceVal = parseFloat(cleanPrice);
              if (!isNaN(priceVal) && priceVal >= 0) {
                mappedData[targetField] = priceVal;
              }
            } 
            // Polish Type - normalize
            else if (targetField === 'polishType') {
              const normalized = normalizePolishType(String(value));
              mappedData[targetField] = normalized || String(value);
            }
            // String fields - everything else
            else {
              mappedData[targetField] = String(value);
            }
          }
        }
        
        // Auto-detect polish type if not set (only for polish imports)
        if (selectedItemType === 'polish' && !mappedData.polishType && mappedData.brand && mappedData.name) {
          const detected = detectPolishType(mappedData.name, mappedData.brand);
          if (detected) {
            mappedData.polishType = detected;
          }
        }
        
        // Skip if missing required fields
        if (!mappedData.brand || !mappedData.name) {
          skipped++;
          skippedList.push({
            rowNum,
            brand: brandStr,
            name: nameStr,
            reason: !mappedData.brand && !mappedData.name 
              ? 'Missing brand and name' 
              : !mappedData.brand 
                ? 'Missing brand' 
                : 'Missing name',
            airtableRecordId: record.id,
          });
          setSkippedCount(skipped);
          setImportProgress(Math.round(((i + 1) / totalRecords) * 100));
          continue;
        }
        
        const key = `${mappedData.brand.toLowerCase()}|${mappedData.name.toLowerCase()}`;
        
        // Skip duplicates
        if (existingKeys.has(key)) {
          skipped++;
          skippedList.push({
            rowNum,
            brand: mappedData.brand,
            name: mappedData.name,
            reason: 'Duplicate (already in collection)',
            airtableRecordId: record.id,
          });
          setSkippedCount(skipped);
          setImportProgress(Math.round(((i + 1) / totalRecords) * 100));
          continue;
        }
        
        setCurrentStatus(`Importing ${mappedData.brand} - ${mappedData.name}...`);
        
        // Build the item document with SELECTED item type (not hardcoded!)
        const itemData: any = {
          itemType: selectedItemType, // FIXED: Use selected type!
          userId: currentUser.uid,
          brand: mappedData.brand,
          brandLower: (mappedData.brand ?? '').toLowerCase(),
          name: mappedData.name,
          nameLower: (mappedData.name ?? '').toLowerCase(),
          quantity: mappedData.quantity || 1,
          dateAdded: Timestamp.now(),
          importedFrom: 'airtable',
          importedAt: Timestamp.now(),
          airtableRecordId: record.id,
        };
        
        // Add common optional fields
        if (mappedData.notes) itemData.notes = mappedData.notes;
        if (mappedData.publicNotes) itemData.publicNotes = mappedData.publicNotes;
        if (mappedData.tags) itemData.tags = mappedData.tags;
        if (mappedData.price) itemData.price = mappedData.price;
        if (mappedData.storage) itemData.storage = mappedData.storage;
        if (mappedData.barcode) itemData.barcode = mappedData.barcode;
        if (mappedData.isFavorite) itemData.isFavorite = true;
        if (mappedData.swatched !== undefined) itemData.swatched = mappedData.swatched;
        if (mappedData.timesUsed !== undefined) itemData.timesUsed = mappedData.timesUsed;
        if (mappedData.lastUsedDate) {
          itemData.lastUsedDate = mappedData.lastUsedDate;
          // A row with a lastUsedDate but no explicit count means "used
          // at least once" — otherwise the item detail would render
          // "0 times used" alongside the date, which reads as broken.
          if (mappedData.timesUsed == null || mappedData.timesUsed <= 0) {
            itemData.timesUsed = 1;
          }
        }
        
        // Add type-specific fields based on selectedItemType
        switch (selectedItemType) {
          case 'polish':
            itemData.category = mappedData.category || mappedData.polishType || 'Nail Lacquer';
            if (mappedData.colorDescription) itemData.colorDescription = mappedData.colorDescription;
            if (mappedData.line) itemData.line = mappedData.line;
            if (mappedData.swatchNumber) itemData.swatchNumber = mappedData.swatchNumber;
            if (mappedData.polishType) itemData.polishType = mappedData.polishType;
            if (mappedData.collection) itemData.collectionName = mappedData.collection;
            if (mappedData.collectionName) itemData.collectionName = mappedData.collectionName;
            if (mappedData.releaseType) itemData.releaseType = mappedData.releaseType;
            if (mappedData.releaseYear) itemData.releaseYear = mappedData.releaseYear;
            if (mappedData.releaseSeason) itemData.releaseSeason = mappedData.releaseSeason;
            if (mappedData.eventSource) itemData.eventName = mappedData.eventSource;
            itemData.eventSource = null; // Clear legacy field (canonical is eventName)
            if (mappedData.eventTheme) itemData.eventTheme = mappedData.eventTheme;
            if (mappedData.colorFamily) itemData.colorFamily = mappedData.colorFamily;
            if (mappedData.finish) itemData.finish = mappedData.finish;
            if (mappedData.holoType) itemData.holoType = mappedData.holoType;
            if (mappedData.particles) itemData.particles = mappedData.particles;
            if (mappedData.specialFeatures) itemData.specialFeatures = mappedData.specialFeatures;
            if (mappedData.utility) itemData.utility = mappedData.utility;
            if (mappedData.colorCharacter) itemData.colorCharacter = mappedData.colorCharacter;
            if (mappedData.baseType) itemData.baseType = mappedData.baseType;
            if (mappedData.colorShift) itemData.colorShift = mappedData.colorShift;
            if (mappedData.onWishlist) itemData.onWishlist = true;
            if (mappedData.isRareLE) itemData.isRareLE = true;
            if (mappedData.isForDestash) itemData.isForDestash = true;
            if (mappedData.destashPrice !== undefined) itemData.destashPrice = mappedData.destashPrice;
            if (mappedData.destashCondition) itemData.destashCondition = mappedData.destashCondition;
            if (mappedData.isoPriority) itemData.isoPriority = mappedData.isoPriority;
            // Release sub-fields
            if (mappedData.boxName) itemData.boxName = mappedData.boxName;
            if (mappedData.boxTheme) itemData.boxTheme = mappedData.boxTheme;
            if (mappedData.groupName) itemData.groupName = mappedData.groupName;
            if (mappedData.groupTheme) itemData.groupTheme = mappedData.groupTheme;
            if (mappedData.exclusiveTo) itemData.exclusiveTo = mappedData.exclusiveTo;
            if (mappedData.exclusiveTheme) itemData.exclusiveTheme = mappedData.exclusiveTheme;
            if (mappedData.charityName) itemData.charityName = mappedData.charityName;
            if (mappedData.charityCause) itemData.charityCause = mappedData.charityCause;
            if (mappedData.conventionName) itemData.conventionName = mappedData.conventionName;
            if (mappedData.collabWith) itemData.collabWith = mappedData.collabWith;
            if (mappedData.collabTheme) itemData.collabTheme = mappedData.collabTheme;
            if (mappedData.setName) itemData.setName = mappedData.setName;
            if (mappedData.setType) itemData.setType = mappedData.setType;
            if (mappedData.isRerelease) itemData.isRerelease = true;
            if (mappedData.originalReleaseYear) itemData.originalReleaseYear = mappedData.originalReleaseYear;
            if (mappedData.originalReleaseSeason) itemData.originalReleaseSeason = mappedData.originalReleaseSeason;
            if (mappedData.releaseNotes) itemData.releaseNotes = mappedData.releaseNotes;
            break;
            
          case 'plate':
            if (mappedData.plateSize) itemData.plateSize = mappedData.plateSize;
            if (mappedData.plateShape) itemData.plateShape = mappedData.plateShape;
            if (mappedData.imageCount) itemData.imageCount = mappedData.imageCount;
            if (mappedData.collection) itemData.collectionName = mappedData.collection;
            break;
            
          case 'transfer':
            if (mappedData.transferType) itemData.transferType = mappedData.transferType;
            if (mappedData.collection) itemData.collectionName = mappedData.collection;
            break;
            
          case 'embellishment':
            if (mappedData.embellishmentType) itemData.embellishmentType = mappedData.embellishmentType;
            if (mappedData.color) itemData.color = mappedData.color;
            if (mappedData.size) itemData.size = mappedData.size;
            break;
            
          case 'tool':
            if (mappedData.toolType) itemData.toolType = mappedData.toolType;
            if (mappedData.material) itemData.material = mappedData.material;
            break;
            
          case 'powder':
            if (mappedData.powderType) itemData.powderType = mappedData.powderType;
            if (mappedData.color) itemData.color = mappedData.color;
            if (mappedData.effect) itemData.effect = mappedData.effect;
            break;
            
          case 'acrylic':
            if (mappedData.acrylicType) itemData.acrylicType = mappedData.acrylicType;
            if (mappedData.color) itemData.color = mappedData.color;
            break;
        }
        
        // Create the document
        const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'items'), itemData);
        
        imported++;
        existingKeys.add(key);
        setImportedCount(imported);
        
        // Handle photos
        if (photoField) {
          const attachments = extractAttachments(record.fields[photoField]);
          
          for (const attachment of attachments) {
            try {
              setCurrentStatus(`📸 Downloading photo for ${mappedData.brand} - ${mappedData.name}...`);
              
              // Download the image (fresh URL!)
              const blob = await downloadAttachment(attachment);
              
              // Compress if needed
              const compressedBlob = await compressImageBlob(blob, 1200, 0.8);
              
              // Check storage AFTER compression (actual size that will be stored)
              if (usedBytes + sessionBytes + compressedBlob.size > limitBytes) {
                storageSkipped++;
                setPhotosSkippedStorage(storageSkipped);
                console.warn(`Skipping photo for ${mappedData.name}: storage limit would be exceeded`);
                continue;
              }
              
              // Upload to Firebase
              const timestamp = Date.now();
              const filename = `photo_${timestamp}.jpg`;
              const storageRef = ref(storage, `users/${currentUser.uid}/items/${docRef.id}/${filename}`);
              
              const snapshot = await uploadBytes(storageRef, compressedBlob, {
                contentType: 'image/jpeg',
                cacheControl: 'public, max-age=31536000, immutable',
              });
              
              const downloadUrl = await getDownloadURL(snapshot.ref);
              
              // Track bytes uploaded this session
              sessionBytes += compressedBlob.size;
              
              // Update the item with the photo
              await updateDoc(doc(db, 'users', currentUser.uid, 'items', docRef.id), {
                photos: arrayUnion({
                  url: downloadUrl,
                  uploadedAt: new Date().toISOString(),
                  source: 'airtable_import',
                  originalFilename: attachment.filename
                })
              });
              
              photos++;
              setPhotoCount(photos);
            } catch (error) {
              console.warn(`Failed to import photo for ${mappedData.name}:`, error);
              photoErrs++;
              setPhotoErrors(photoErrs);
            }
            
            // Small delay between photos
            await new Promise(resolve => setTimeout(resolve, 200));
          }
        }
        
        setImportProgress(Math.round(((i + 1) / totalRecords) * 100));
      }
      
      setSkippedRecords(skippedList);
      setCurrentStatus('');
      setCurrentStep('complete');
    } catch (error) {
      console.error('Import error:', error);
      setImportError('An error occurred during import. Some items may have been imported.');
      setSkippedRecords(skippedList);
      setCurrentStep('complete');
    }
  };

  const missingRequired = getMissingRequired();
  const duplicateMappings = getDuplicateMappings();
  const recordsWithPhotos = getRecordsWithPhotos();
  const itemTypeInfo = ITEM_TYPE_INFO[selectedItemType];

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
              <Database className="icon-md text-yellow-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-glass font-body">Import from Airtable</h2>
              <p className="text-sm text-glass-secondary font-body">
                Direct API connection with fresh photo URLs
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-lg transition-default"
          >
            <X className="icon-md text-glass-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* STEP 1: API Key */}
          {currentStep === 'api-key' && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 flex items-center justify-center mx-auto mb-4">
                  <Key className="icon-lg text-yellow-400" />
                </div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  Enter Your Airtable API Key
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  We need your API key to access your Airtable data
                </p>
              </div>
              
              {/* Security notice */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="icon-md text-green-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-bold text-green-300 mb-1 font-body">Your API key is safe</p>
                    <ul className="text-green-200/80 space-y-1 font-body">
                      <li>• Used only in your browser — never sent to our servers</li>
                      <li>• Read-only access — we only fetch data, never modify</li>
                      <li>• Session only — cleared when you close this window</li>
                      <li>• You choose exactly which base to import</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* API Key input */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-glass font-body">
                  Personal Access Token
                </label>
                <div className="relative">
                  <input
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="pat..."
                    className="input-glass w-full pr-10 font-mono text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-glass-muted hover:text-glass"
                  >
                    {showApiKey ? <EyeOff className="icon-sm" /> : <Eye className="icon-sm" />}
                  </button>
                </div>
                {keyError && (
                  <p className="text-sm text-status-error flex items-center gap-1">
                    <AlertCircle className="icon-sm" />
                    {keyError}
                  </p>
                )}
              </div>
              
              {/* How to get API key */}
              <div className="bg-slate-800/60 rounded-xl p-4">
                <h4 className="font-bold text-glass mb-2 flex items-center gap-2 font-body">
                  <HelpCircle className="icon-sm" />
                  How to get your API key
                </h4>
                <ol className="text-sm text-glass-secondary space-y-2 font-body">
                  <li>1. Go to <a href="https://airtable.com/create/tokens" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">airtable.com/create/tokens <ExternalLink className="icon-xs inline" /></a></li>
                  <li>2. Click "Create new token"</li>
                  <li>3. Give it a name like "Polish Import"</li>
                  <li>4. Add scopes: <code className="bg-slate-700 px-1 rounded">data.records:read</code> and <code className="bg-slate-700 px-1 rounded">schema.bases:read</code></li>
                  <li>5. Select the base(s) you want to import from</li>
                  <li>6. Copy the token and paste it above</li>
                </ol>
              </div>
              
              {/* Continue button */}
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleValidateKey}
                  disabled={!apiKey.trim() || validatingKey}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body"
                >
                  {validatingKey ? (
                    <>
                      <Loader2 className="icon-sm animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      Connect to Airtable
                      <ArrowRight className="icon-sm" />
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Select Base */}
          {currentStep === 'select-base' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  Select a Base
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  Choose the Airtable base containing your collection
                </p>
              </div>
              
              {loadingBases ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-yellow-400 mx-auto animate-spin mb-4" />
                  <p className="text-glass-secondary font-body">Loading your bases...</p>
                </div>
              ) : bases.length === 0 ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
                  <p className="text-glass-secondary font-body">No bases found. Make sure your API key has access to at least one base.</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {bases.map(base => (
                    <button
                      key={base.id}
                      onClick={() => handleSelectBase(base)}
                      className="flex items-center gap-4 p-4 bg-slate-800/60 rounded-xl border border-slate-600 hover:border-yellow-500/50 hover:bg-slate-800 transition-all text-left"
                    >
                      <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                        <Database className="icon-md text-yellow-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-glass truncate font-body">{base.name}</p>
                        <p className="text-sm text-glass-muted font-body">{base.permissionLevel} access</p>
                      </div>
                      <ArrowRight className="icon-md text-glass-muted" />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Back button */}
              <div className="flex justify-start pt-4">
                <button
                  onClick={() => setCurrentStep('api-key')}
                  className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Select Table */}
          {currentStep === 'select-table' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  Select a Table
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  Choose the table in <strong>{selectedBase?.name}</strong> that contains your items
                </p>
              </div>
              
              {loadingTables ? (
                <div className="text-center py-12">
                  <Loader2 className="w-12 h-12 text-yellow-400 mx-auto animate-spin mb-4" />
                  <p className="text-glass-secondary font-body">Loading tables...</p>
                </div>
              ) : (
                <div className="grid gap-3">
                  {tables.map(table => (
                    <button
                      key={table.id}
                      onClick={() => handleSelectTable(table)}
                      disabled={loadingRecords}
                      className="flex items-center gap-4 p-4 bg-slate-800/60 rounded-xl border border-slate-600 hover:border-yellow-500/50 hover:bg-slate-800 transition-all text-left disabled:opacity-50"
                    >
                      <div className="w-12 h-12 rounded-xl bg-slate-700/50 flex items-center justify-center flex-shrink-0">
                        <Table className="icon-md text-glass-muted" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-glass truncate font-body">{table.name}</p>
                        <p className="text-sm text-glass-muted font-body">{table.fields.length} fields</p>
                      </div>
                      {loadingRecords && selectedTable?.id === table.id ? (
                        <div className="text-right">
                          <Loader2 className="icon-md text-yellow-400 animate-spin" />
                          <p className="text-xs text-glass-muted mt-1">
                            {resolvingLinkedRecords
                              ? 'Resolving linked data...'
                              : `${recordsFetched} records`}
                          </p>
                        </div>
                      ) : (
                        <ArrowRight className="icon-md text-glass-muted" />
                      )}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Back button */}
              <div className="flex justify-start pt-4">
                <button
                  onClick={() => setCurrentStep('select-base')}
                  className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Select Item Type (NEW!) */}
          {currentStep === 'select-type' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  What are you importing?
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  Found <strong>{records.length}</strong> records in <strong>{selectedTable?.name}</strong>.
                  Choose what type of items these are.
                </p>
              </div>

              {/* Linked records detection */}
              {linkedFields.size > 0 && (
                <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/40 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="icon-md text-blue-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-glass font-body">🔗 Linked records resolved!</p>
                      <p className="text-sm text-glass-secondary font-body">
                        Found linked fields: <strong>{Array.from(linkedFields.keys()).join(', ')}</strong>
                      </p>
                      <p className="text-sm text-blue-300 mt-1 font-body">
                        We've automatically fetched the display names from your linked tables.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Item type grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {(Object.entries(ITEM_TYPE_INFO) as [ImportItemType, typeof ITEM_TYPE_INFO[ImportItemType]][]).map(([type, info]) => (
                  <button
                    key={type}
                    onClick={() => setSelectedItemType(type)}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      selectedItemType === type
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-slate-600 hover:border-slate-500 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="text-2xl mb-1">{info.emoji}</div>
                    <div className="font-semibold text-glass text-sm font-body">{info.label}</div>
                    <div className="text-xs text-glass-muted mt-1 font-body">{info.description}</div>
                  </button>
                ))}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep('select-table')}
                  className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep('mapping')}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-default font-body"
                >
                  Continue to Field Mapping
                  <ArrowRight className="icon-sm" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Field Mapping */}
          {currentStep === 'mapping' && analysis && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  Map Your Fields
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  Importing as <strong>{itemTypeInfo.emoji} {itemTypeInfo.label}</strong>.
                  Match your Airtable columns to the correct fields.
                </p>
              </div>
              
              {/* Important instruction */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <HelpCircle className="icon-md text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-bold text-blue-300 font-body">Map Brand and Name first!</p>
                    <p className="text-blue-200/80 font-body">
                      Find your columns that contain brand and name data, then select the matching field from each dropdown.
                      Look for the 💡 suggestions as hints.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Photo detection */}
              {analysis.attachmentFields.length > 0 && (
                <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/40 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Image className="icon-md text-purple-300 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-glass font-body">📸 Photo attachments detected!</p>
                      <p className="text-sm text-glass-secondary font-body">
                        Found attachment fields: <strong>{analysis.attachmentFields.join(', ')}</strong>
                      </p>
                      <p className="text-sm text-purple-300 mt-1 font-body">
                        Map one to "Photos" to import your images with fresh URLs!
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Missing required warning */}
              {missingRequired.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="icon-md text-amber-400 flex-shrink-0" />
                    <p className="text-amber-300 font-body">
                      <strong>Required fields not mapped:</strong> {missingRequired.map(f => targetFields[f]?.label || f).join(', ')}
                    </p>
                  </div>
                </div>
              )}
              
              {/* Mapping Summary - Visual overview */}
              <div className="bg-slate-800/40 rounded-xl p-4 border border-slate-600/50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-glass text-sm font-body">Mapping Summary</h4>
                  <span className="text-xs text-glass-muted font-body">
                    {Object.values(fieldMapping).filter(Boolean).length} of {analysis?.fields.length || 0} columns mapped
                  </span>
                </div>
                
                {/* Visual progress bar */}
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
                  <div 
                    className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-300"
                    style={{ 
                      width: `${analysis?.fields.length 
                        ? (Object.values(fieldMapping).filter(Boolean).length / analysis.fields.length) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
                
                {/* Quick stats */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-green-500/10 rounded-lg p-2">
                    <div className="text-lg font-bold text-green-400">
                      {Object.values(fieldMapping).filter(Boolean).length}
                    </div>
                    <div className="text-xs text-green-300/70 font-body">Mapped</div>
                  </div>
                  <div className="bg-slate-600/30 rounded-lg p-2">
                    <div className="text-lg font-bold text-glass-muted">
                      {(analysis?.fields.length || 0) - Object.values(fieldMapping).filter(Boolean).length}
                    </div>
                    <div className="text-xs text-glass-muted font-body">Skipped</div>
                  </div>
                  <div className={`rounded-lg p-2 ${missingRequired.length > 0 ? 'bg-amber-500/10' : 'bg-green-500/10'}`}>
                    <div className={`text-lg font-bold ${missingRequired.length > 0 ? 'text-amber-400' : 'text-green-400'}`}>
                      {Object.keys(targetFields).filter(k => targetFields[k].required).length - missingRequired.length}
                      /{Object.keys(targetFields).filter(k => targetFields[k].required).length}
                    </div>
                    <div className={`text-xs font-body ${missingRequired.length > 0 ? 'text-amber-300/70' : 'text-green-300/70'}`}>
                      Required
                    </div>
                  </div>
                </div>
                
                {/* Mapped fields list */}
                {Object.values(fieldMapping).filter(Boolean).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-600/50">
                    <p className="text-xs text-glass-muted mb-2 font-body">Currently mapped:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(fieldMapping)
                        .filter(([_, target]) => target)
                        .map(([source, target]) => (
                          <span 
                            key={source} 
                            className={`text-xs px-2 py-1 rounded-full font-body ${
                              targetFields[target!]?.required 
                                ? 'bg-green-500/20 text-green-300' 
                                : 'bg-slate-600/50 text-glass-secondary'
                            }`}
                          >
                            {targetFields[target!]?.label || target}
                            {targetFields[target!]?.required && ' *'}
                          </span>
                        ))
                      }
                    </div>
                  </div>
                )}
              </div>
              
              {/* Duplicate mapping warning */}
              {duplicateMappings.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="icon-md text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-300 font-body">Duplicate mappings detected!</p>
                      <p className="text-sm text-red-200/80 font-body">
                        Multiple columns are mapped to the same field. This will cause data issues:
                      </p>
                      <ul className="text-sm mt-1 text-red-200/80 font-body">
                        {duplicateMappings.map(({ target, sources }) => (
                          <li key={target}>
                            • <strong>{targetFields[target]?.label || target}</strong> ← {sources.join(', ')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Field mapping grid */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {analysis.fields.map(field => {
                  const currentMapping = fieldMapping[field.name];
                  const hasSuggestion = field.suggestedMapping && !currentMapping;
                  
                  return (
                    <div key={field.name} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-slate-800/60 rounded-lg">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-glass font-body">
                          {field.name}
                          {field.type === 'multipleAttachments' && (
                            <span className="ml-2 text-xs px-2 py-0.5 bg-purple-500/30 text-purple-300 rounded-full">
                              📎 Attachments
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-glass-muted truncate">
                          {field.sampleValues.length > 0 
                            ? field.sampleValues.join(', ')
                            : '(no values)'}
                        </p>
                        {/* Show suggestion as a hint */}
                        {hasSuggestion && (
                          <p className="text-xs text-yellow-400/80 mt-1 font-body">
                            💡 Looks like: {targetFields[field.suggestedMapping!]?.label || field.suggestedMapping}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4">
                        <ArrowRight className="icon-sm text-glass-muted flex-shrink-0 hidden sm:block" />
                        <select
                          value={currentMapping || ''}
                          onChange={(e) => updateMapping(field.name, e.target.value || null)}
                          className={`select-glass flex-1 sm:flex-none sm:min-w-[200px] font-body ${
                            !currentMapping ? 'text-glass-muted italic' : ''
                          }`}
                        >
                          <option value="">-- Skip this column --</option>
                          <optgroup label="Required Fields">
                            {Object.entries(targetFields)
                              .filter(([_, config]) => config.required)
                              .map(([key, config]) => (
                                <option key={key} value={key}>
                                  {config.label} *
                                </option>
                              ))}
                          </optgroup>
                          <optgroup label="Optional Fields">
                            {Object.entries(targetFields)
                              .filter(([_, config]) => !config.required)
                              .map(([key, config]) => (
                                <option key={key} value={key}>
                                  {config.label}
                                </option>
                              ))}
                          </optgroup>
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep('select-type')}
                  className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep('preview')}
                  disabled={missingRequired.length > 0 || duplicateMappings.length > 0}
                  className="flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-default disabled:opacity-50 disabled:cursor-not-allowed font-body"
                >
                  Preview Import
                  <ArrowRight className="icon-sm" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: Preview */}
          {currentStep === 'preview' && analysis && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-glass mb-2 font-body">
                  Ready to Import!
                </h3>
                <p className="text-glass-secondary text-sm font-body">
                  Review what will be imported from Airtable
                </p>
              </div>
              
              {/* Summary cards */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-800/60 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-glass mb-1">{records.length}</p>
                  <p className="text-sm text-glass-secondary font-body">{itemTypeInfo.emoji} {itemTypeInfo.label}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl p-4 text-center border border-purple-500/30">
                  <p className="text-3xl font-bold text-purple-300 mb-1">{recordsWithPhotos}</p>
                  <p className="text-sm text-glass-secondary font-body">With Photos</p>
                </div>
              </div>
              
              {/* Photo import note */}
              {recordsWithPhotos > 0 && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="icon-md text-green-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-green-300 font-body">Photos will be imported with fresh URLs!</p>
                      <p className="text-sm text-green-200/80 font-body">
                        Since we're using the API, photo URLs are fetched in real-time and won't expire during import.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Preview table */}
              <div className="bg-slate-800/60 rounded-xl border border-slate-600 overflow-hidden">
                <div className="overflow-x-auto max-h-[200px]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/80 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-glass-secondary font-body">Brand</th>
                        <th className="px-3 py-2 text-left text-glass-secondary font-body">Name</th>
                        <th className="px-3 py-2 text-left text-glass-secondary font-body">Photo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {records.slice(0, 10).map((record) => {
                        const brandField = Object.entries(fieldMapping).find(([_, t]) => t === 'brand')?.[0];
                        const nameField = Object.entries(fieldMapping).find(([_, t]) => t === 'name')?.[0];
                        const photoField = getPhotoField();

                        // Get raw values
                        let brandValue = brandField ? record.fields[brandField] : null;
                        let nameValue = nameField ? record.fields[nameField] : null;

                        // Resolve linked records if cache is available
                        if (brandField && brandValue) {
                          brandValue = resolveLinkedRecordValue(brandValue, brandField, linkedFields, linkedRecordCache);
                        }
                        if (nameField && nameValue) {
                          nameValue = resolveLinkedRecordValue(nameValue, nameField, linkedFields, linkedRecordCache);
                        }

                        const brand = brandValue != null ? String(brandValue) : '-';
                        const name = nameValue != null ? String(nameValue) : '-';
                        const hasPhoto = photoField && extractAttachments(record.fields[photoField]).length > 0;
                        
                        return (
                          <tr key={record.id} className="border-t border-slate-700">
                            <td className="px-3 py-2 text-glass font-body">{brand}</td>
                            <td className="px-3 py-2 text-glass font-body">{name}</td>
                            <td className="px-3 py-2">
                              {hasPhoto ? (
                                <span className="text-purple-300 flex items-center gap-1">
                                  <Image className="icon-xs" /> Yes
                                </span>
                              ) : (
                                <span className="text-glass-muted">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {records.length > 10 && (
                  <p className="px-3 py-2 text-xs text-glass-muted text-center bg-slate-800/50">
                    Showing 10 of {records.length} records
                  </p>
                )}
              </div>
              
              {/* Navigation */}
              <div className="flex justify-between pt-4">
                <button
                  onClick={() => setCurrentStep('mapping')}
                  className="flex items-center gap-2 px-4 py-2 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  <ArrowLeft className="icon-sm" />
                  Back
                </button>
                <button
                  onClick={handleProceedToReview}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-400 transition-default font-body"
                >
                  Review Data Quality
                  <ArrowRight className="icon-sm" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 7: Review */}
          {currentStep === 'review' && mappedRowsForReview.length > 0 && (
            <PreImportReview
              mappedRows={mappedRowsForReview}
              onBack={() => setCurrentStep('preview')}
              onProceed={(updatedRows) => {
                setMappedRowsForReview(updatedRows);
                handleImport();
              }}
            />
          )}

          {/* STEP 8: Importing */}
          {currentStep === 'importing' && (
            <div className="py-12 text-center space-y-6">
              <Loader2 className="w-16 h-16 text-yellow-400 mx-auto animate-spin" />
              <div>
                <h3 className="text-xl font-bold text-glass mb-2 font-body">
                  Importing {itemTypeInfo.emoji} {itemTypeInfo.label}...
                </h3>
                <p className="text-glass-secondary font-body">
                  Please don't close this window
                </p>
                {currentStatus && (
                  <p className="text-sm text-purple-300 mt-2 font-body">
                    {currentStatus}
                  </p>
                )}
              </div>
              
              {/* Progress bar */}
              <div className="max-w-md mx-auto">
                <div className="flex justify-between text-sm text-glass-secondary mb-2">
                  <span>Progress</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="progress-bar-track">
                  <div
                    className="progress-bar-fill bg-yellow-500"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm mt-2">
                  <span className="text-status-success">{importedCount} imported</span>
                  {skippedCount > 0 && <span className="text-status-warning">{skippedCount} skipped</span>}
                  {photoCount > 0 && <span className="text-purple-300">📸 {photoCount} photos</span>}
                </div>
              </div>
            </div>
          )}

          {/* STEP 9: Complete */}
          {currentStep === 'complete' && (
            <div className="py-8 text-center space-y-6">
              {importError ? (
                <AlertCircle className="w-16 h-16 text-amber-500 mx-auto" />
              ) : (
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto" />
              )}
              
              <div>
                <h3 className="text-xl font-bold text-glass mb-2 font-body">
                  {importError ? 'Import Completed with Warnings' : '🎉 Import Complete!'}
                </h3>
                {importError ? (
                  <p className="text-status-warning font-body">{importError}</p>
                ) : (
                  <p className="text-glass-secondary font-body">
                    Successfully imported <strong className="text-status-success">{importedCount}</strong> {itemTypeInfo.label.toLowerCase()}
                    {skippedCount > 0 && (
                      <span> (<strong className="text-amber-400">{skippedCount}</strong> skipped)</span>
                    )}
                  </p>
                )}
                
                {/* Photo results */}
                {(photoCount > 0 || photoErrors > 0) && (
                  <div className="mt-4 p-4 bg-purple-500/10 rounded-lg inline-block">
                    <p className="text-purple-300 font-body">
                      📸 <strong>{photoCount}</strong> photos imported
                      {photoErrors > 0 && (
                        <span className="text-amber-300"> ({photoErrors} failed)</span>
                      )}
                    </p>
                  </div>
                )}
                
                {/* Storage limit warning */}
                {photosSkippedStorage > 0 && (
                  <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl max-w-md mx-auto">
                    <div className="flex items-start gap-3 text-left">
                      <HardDrive className="icon-md text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-bold text-amber-300 font-body">
                          {photosSkippedStorage} photo{photosSkippedStorage !== 1 ? 's' : ''} skipped — storage full
                        </p>
                        <p className="text-sm text-amber-200/80 font-body mb-3">
                          Your items were imported successfully! Upgrade your plan to add the remaining photos.
                        </p>
                        {nextTier && (
                          <button
                            onClick={() => setShowUpgradeModal(true)}
                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-default"
                          >
                            Upgrade Storage
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Error report download (NEW!) */}
              {skippedRecords.length > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 max-w-md mx-auto">
                  <div className="flex items-start gap-3 text-left">
                    <FileText className="icon-md text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-bold text-amber-300 font-body">
                        {skippedRecords.length} items were skipped
                      </p>
                      <p className="text-sm text-amber-200/80 font-body mb-3">
                        Download a report to see which items need manual attention.
                      </p>
                      <button
                        onClick={downloadErrorReport}
                        className="flex items-center gap-2 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-lg text-sm font-medium transition-default"
                      >
                        <Download className="icon-sm" />
                        Download Error Report (CSV)
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-col items-center gap-3 pt-4">
                <button
                  onClick={() => {
                    onComplete();
                    onClose();
                  }}
                  className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 bg-yellow-500 text-black rounded-lg font-semibold hover:bg-yellow-400 transition-default font-body"
                >
                  View Your Collection
                  <ArrowRight className="icon-sm" />
                </button>
                
                {/* Import Another Base - keeps API key */}
                <button
                  onClick={() => {
                    // Reset state but keep API key
                    setSelectedBase(null);
                    setSelectedTable(null);
                    setTables([]);
                    setRecords([]);
                    setAnalysis(null);
                    setFieldMapping({});
                    setImportedCount(0);
                    setSkippedCount(0);
                    setPhotoCount(0);
                    setPhotoErrors(0);
                    setImportError(null);
                    setSkippedRecords([]);
                    setMappedRowsForReview([]);
                    setPhotosSkippedStorage(0);
                    setCurrentStep('select-base');
                  }}
                  className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 bg-slate-700 text-white rounded-lg font-semibold hover:bg-slate-600 transition-default font-body"
                >
                  <Database className="icon-sm" />
                  Import Another Base
                </button>
                <p className="text-xs text-glass-muted font-body max-w-xs text-center">
                  Your API key is kept in memory for this session only — it's never saved to our servers and will be cleared when you close this window.
                </p>
                
                <button
                  onClick={onClose}
                  className="flex items-center justify-center gap-2 w-full max-w-xs px-6 py-3 text-glass-secondary hover:text-glass font-medium font-body"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Storage Upgrade Modal */}
      {showUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          highlightTier={nextTier?.id}
        />
      )}
    </div>
  );
}
